/**
 * FlowVariablePicker — one control for choosing (or naming) a FLOW VARIABLE.
 *
 * Used wherever an author names a variable an automation writes or reads: the
 * "Get User Input" node, and the API-connection response mapping. It offers the
 * three affordances the registry exists for:
 *   • SELECT an existing name from the workspace flow-variable registry
 *   • REFRESH the registry (an author may have just created one in another tab)
 *   • CREATE NEW — opens Settings › Custom Fields › Flow variables in a new tab
 *
 * It is free-solo on purpose: the registry is advisory, so an author can still
 * type a brand-new name inline and nothing blocks on it being unregistered.
 * The value is always the bare variable name (what goes inside {{…}}).
 */
import React from 'react';
import { Autocomplete, TextField, IconButton, Tooltip, Link, Box, CircularProgress, Typography } from '@mui/material';
import { Refresh, AddCircleOutline, BookmarkAddOutlined } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material';

/** Mirror of the server's flow-variable name rule (field-definition.controller). */
const VALID_NAME = /^[A-Za-z_][A-Za-z0-9_]{0,59}$/;

/** Minimal shape — the registry rows mapped down to what the picker shows. */
export interface FlowVariableOption {
  key: string;
  label?: string | undefined;
  description?: string | undefined;
  type?: string | undefined;
}

interface Props {
  value: string;
  onChange: (name: string) => void;
  options: FlowVariableOption[];
  loading?: boolean;
  onRefresh: () => void;
  error?: boolean;
  placeholder?: string;
  /** Styling for the inner TextField so the control fits its surface. */
  fieldSx?: SxProps<Theme>;
  /** Monospace the input + options (variable names read as code). */
  mono?: React.CSSProperties;
  /** Where "Create new" opens. Defaults to the Flow-variables registry tab. */
  createHref?: string;
  /** The dropdown portals to document.body (outside the canvas's dark scope) and
   *  the app theme is light — set this on the dark canvas so the popup doesn't
   *  render as a stray light panel over the flow. */
  dark?: boolean;
  /** Compact: just the autocomplete, no refresh button or "New variable" link.
   *  Used in tight rows (response mapping) where those live once at section level. */
  compact?: boolean;
  /** When set, a one-click "save to the registry" button appears whenever the
   *  typed name is a valid variable name that isn't registered yet. Resolve the
   *  promise after the registry is refreshed so the button disappears. */
  onCreate?: (name: string) => Promise<void> | void;
}

const DEFAULT_CREATE_HREF = '/admin/settings?tab=flow-vars';

const FlowVariablePicker: React.FC<Props> = ({
  value,
  onChange,
  options,
  loading,
  onRefresh,
  error,
  placeholder = 'variable_name',
  fieldSx,
  mono,
  createHref = DEFAULT_CREATE_HREF,
  dark,
  compact,
  onCreate,
}) => {
  const [saving, setSaving] = React.useState(false);
  const trimmed = value.trim();
  // Offer "save as variable" only for a valid, not-yet-registered name.
  const canSave =
    !!onCreate && !saving && VALID_NAME.test(trimmed) && !options.some((o) => o.key === trimmed);
  const doSave = async () => {
    if (!onCreate || saving) return;
    setSaving(true);
    try {
      await onCreate(trimmed);
    } finally {
      setSaving(false);
    }
  };
  const darkPaper = dark
    ? {
        paper: {
          sx: {
            bgcolor: 'var(--wf-surface-2, #161b25)',
            color: 'var(--wf-text, #e8ecf4)',
            border: '1px solid #2e3644',
            '& .MuiAutocomplete-noOptions': { color: 'var(--wf-text-dim, #939eb2)' },
          },
        },
      }
    : undefined;
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Autocomplete
          freeSolo
          fullWidth
          size="small"
          options={options}
          loading={!!loading}
          // Value is a plain string; keep the input controlled so a typed, not-yet
          // registered name survives (the registry is advisory, never a gate).
          inputValue={value}
          onInputChange={(_, v) => onChange(v)}
          {...(darkPaper ? { slotProps: darkPaper } : {})}
          {...(dark ? { sx: { '& .MuiAutocomplete-option.Mui-focused': { bgcolor: '#1c222e !important' } } } : {})}
          getOptionLabel={(o) => (typeof o === 'string' ? o : o.key)}
          filterOptions={(opts, state) => {
            const q = state.inputValue.trim().toLowerCase();
            if (!q) return opts;
            return opts.filter((o) => {
              const k = typeof o === 'string' ? o : o.key;
              const l = typeof o === 'string' ? '' : o.label ?? '';
              return k.toLowerCase().includes(q) || l.toLowerCase().includes(q);
            });
          }}
          renderOption={(props, o) => {
            const opt = o as FlowVariableOption;
            return (
              <li {...props} key={opt.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={mono}>{opt.key}</span>
                {(opt.description || opt.type) && (
                  <span style={{ opacity: 0.6, fontSize: 12 }}>{opt.description || opt.type}</span>
                )}
              </li>
            );
          }}
          renderInput={(p) => (
            <TextField
              {...(p as any)}
              placeholder={placeholder}
              error={error}
              sx={fieldSx}
              inputProps={{ ...(p as any).inputProps, style: mono }}
            />
          )}
        />
        {(canSave || saving) && (
          <Tooltip title={saving ? 'Saving…' : `Save “${trimmed}” to your variables`}>
            <span>
              <IconButton size="small" onClick={doSave} disabled={saving} sx={{ mt: 0.25, color: 'primary.main' }}>
                {saving ? <CircularProgress size={15} /> : <BookmarkAddOutlined fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {!compact && (
          <Tooltip title="Refresh variables">
            <span>
              <IconButton size="small" onClick={onRefresh} disabled={!!loading} sx={{ mt: 0.25 }}>
                {loading ? <CircularProgress size={15} /> : <Refresh fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      {!compact && (
        <Link
          href={createHref}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ mt: 0.5, fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          <AddCircleOutline sx={{ fontSize: 13 }} />
          <Typography component="span" sx={{ fontSize: 11.5 }}>
            New variable
          </Typography>
        </Link>
      )}
    </Box>
  );
};

export default FlowVariablePicker;
